import { useParams, Navigate, Link } from 'react-router-dom';
import { SEO } from '../../../components/seo/SEO';
import { getSeriesById, defaultSeriesList } from '../../../lib/seriesData';
import { getBaseUrl } from '../../../lib/utils';

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

export default function SeriesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const series = id ? getSeriesById(id) : null;

  if (!series) return <Navigate to="/series" replace />;

  const baseUrl = getBaseUrl();
  const publishedCount = series.articles.filter((a) => a.status === 'published').length;
  const totalCount = series.articles.length;
  const progressPct = Math.round((publishedCount / totalCount) * 100);

  const pickupArticles = series.articles
    .filter((a) => a.status === 'published')
    .slice(0, 2);

  const otherSeries = defaultSeriesList.filter((s) => s.id !== series.id);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${series.seriesName} | つくってなんぼ`,
    description: series.description,
    url: `${baseUrl}/series/${id}`,
  };

  return (
    <>
      <SEO
        title={`${series.seriesName} | つくってなんぼ`}
        description={series.description}
        canonical={`/series/${id}`}
        jsonLd={jsonLd}
      />

      <div className="min-h-screen bg-[#FDFAF6]">

        {/* ── Series header ── */}
        <div className="pt-24 sm:pt-32 pb-8 border-b border-[#E8E0D5]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-xs text-[#9A8E84] mb-5">
              <Link to="/" className="hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>Home</Link>
              <i className="ri-arrow-right-s-line text-[#C8BFB5]"></i>
              <Link to="/series" className="hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>シリーズ</Link>
              <i className="ri-arrow-right-s-line text-[#C8BFB5]"></i>
              <span className="text-[#7A6E62] line-clamp-1" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{series.seriesName}</span>
            </nav>

            {/* Status badge */}
            <div className="flex items-center gap-2 mb-4">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#5B7B6F] bg-[#EEF4F2] border border-[#5B7B6F]/20 px-2.5 py-1 rounded-full" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#5B7B6F] inline-block"></span>
                進行中
              </span>
              <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                {publishedCount}/{totalCount}本公開
              </span>
            </div>

            <p className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              Series
            </p>
            <h1
              className="text-2xl sm:text-3xl font-bold text-[#2C2416] mb-3 leading-snug"
              style={{ fontFamily: "'Noto Serif JP', 'Georgia', serif", letterSpacing: '-0.01em' }}
            >
              {series.seriesName}
            </h1>
            <p className="text-sm text-[#4A3F35] leading-relaxed mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              {series.description}
            </p>

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5">
              {series.tags.map((tag) => (
                <span key={tag} className="text-[11px] text-[#7A6E62] bg-[#F0EBE3] border border-[#E8E0D5] px-2 py-0.5 rounded" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* ── Progress block ── */}
        <div className="py-7 bg-[#F5EFE6] border-b border-[#E8E0D5]">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              現在地
            </p>
            <div className="space-y-4">
              {/* Progress bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-[#7A6E62]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>進行状況</span>
                  <span className="text-xs font-semibold text-[#2C2416]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{publishedCount}/{totalCount}本</span>
                </div>
                <div className="h-1.5 bg-[#E8E0D5] rounded-full overflow-hidden">
                  <div className="h-full bg-[#5B7B6F] rounded-full transition-all" style={{ width: `${progressPct}%` }}></div>
                </div>
              </div>

              {/* Current / Next / Goal */}
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-[#5B7B6F] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#5B7B6F] mb-0.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>いまここ</p>
                    <p className="text-sm text-[#4A3F35] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{series.currentProgress}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-arrow-right-circle-line text-[#9A8E84] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#9A8E84] mb-0.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>次にやること</p>
                    <p className="text-sm text-[#4A3F35] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{series.nextStep}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-flag-line text-[#C8BFB5] text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-[#C8BFB5] mb-0.5" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>ゴール</p>
                    <p className="text-sm text-[#7A6E62] leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{series.goal}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

          {/* ── Pickup ── */}
          {pickupArticles.length > 0 && (
            <div className="mb-10">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#5B7B6F] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                まず読むならこれ
              </p>
              <div className="space-y-4">
                {pickupArticles.map((article, index) => (
                  <Link
                    key={article.slug}
                    to={`/articles/${article.slug}`}
                    className="flex items-start gap-4 group cursor-pointer"
                  >
                    <span className="text-xl font-bold text-[#D4C9BE] leading-none mt-0.5 flex-shrink-0 w-6 text-right" style={{ fontFamily: "'Georgia', serif" }}>
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      {article.label && (
                        <span className="text-[10px] font-semibold text-[#5B7B6F] bg-[#EEF4F2] px-1.5 py-0.5 rounded mr-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                          {article.label}
                        </span>
                      )}
                      <h3 className="text-sm sm:text-base font-semibold text-[#2C2416] leading-snug group-hover:text-[#5B7B6F] transition-colors mt-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                        {article.title}
                      </h3>
                      {article.excerpt && (
                        <p className="text-xs text-[#7A6E62] mt-1 line-clamp-1 leading-relaxed" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                          {article.excerpt}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* ── Article list ── */}
          <div className="mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              記事一覧
            </p>
            <div className="space-y-0">
              {series.articles.map((article, index) => {
                const isPublished = article.status === 'published';
                const isLast = index === series.articles.length - 1;
                return (
                  <div key={article.num} className="flex gap-4">
                    {/* Timeline line */}
                    <div className="flex flex-col items-center flex-shrink-0 w-8">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border-2 ${isPublished ? 'bg-[#5B7B6F] border-[#5B7B6F]' : 'bg-[#FDFAF6] border-[#D4C9BE]'}`}>
                        {isPublished
                          ? <i className="ri-check-line text-white text-xs"></i>
                          : <span className="text-[10px] text-[#C8BFB5] font-bold">{article.num}</span>
                        }
                      </div>
                      {!isLast && (
                        <div className={`w-px flex-1 mt-1 mb-1 ${isPublished ? 'bg-[#5B7B6F]/30' : 'bg-[#E8E0D5]'}`} style={{ minHeight: '24px' }}></div>
                      )}
                    </div>

                    {/* Content */}
                    <div className={`flex-1 min-w-0 pb-5 ${isLast ? '' : ''}`}>
                      {isPublished ? (
                        <Link
                          to={`/articles/${article.slug}`}
                          className="block group cursor-pointer"
                        >
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            {article.label && (
                              <span className="text-[10px] font-semibold text-[#5B7B6F] bg-[#EEF4F2] px-1.5 py-0.5 rounded" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                                {article.label}
                              </span>
                            )}
                            {article.date && (
                              <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                                {formatDate(article.date)}
                              </span>
                            )}
                            {article.duration && (
                              <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                                · {article.duration}
                              </span>
                            )}
                          </div>
                          <h3 className="text-sm font-semibold text-[#2C2416] leading-snug group-hover:text-[#5B7B6F] transition-colors mb-1" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                            {article.title}
                          </h3>
                          {article.excerpt && (
                            <p className="text-xs text-[#7A6E62] leading-relaxed line-clamp-1" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                              {article.excerpt}
                            </p>
                          )}
                        </Link>
                      ) : (
                        <div className="opacity-50">
                          <div className="flex items-center gap-2 mb-1">
                            {article.label && (
                              <span className="text-[10px] font-semibold text-[#9A8E84] bg-[#F0EBE3] px-1.5 py-0.5 rounded" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                                {article.label}
                              </span>
                            )}
                            <span className="text-[10px] text-[#C8BFB5] font-medium" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>準備中</span>
                          </div>
                          <p className="text-sm text-[#9A8E84] leading-snug" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                            {article.title}
                          </p>
                          {article.excerpt && (
                            <p className="text-xs text-[#C8BFB5] leading-relaxed mt-1 line-clamp-1" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                              {article.excerpt}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Upcoming topics ── */}
          {series.upcomingTopics.length > 0 && (
            <div className="mb-10 bg-[#F8F3EC] border border-[#E8E0D5] rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-draft-line text-[#5B7B6F] text-sm"></i>
                <p className="text-xs font-semibold text-[#5B7B6F]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                  今後の予定
                </p>
              </div>
              <ul className="space-y-2">
                {series.upcomingTopics.map((topic, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[#7A6E62]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    <span className="text-[#C8BFB5] mt-0.5 flex-shrink-0">—</span>
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* ── Other series ── */}
          {otherSeries.length > 0 && (
            <div className="mb-10 border-t border-[#EDE7DF] pt-8">
              <p className="text-xs font-semibold tracking-widest uppercase text-[#9A8E84] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                他のシリーズ
              </p>
              <div className="space-y-3">
                {otherSeries.map((s) => {
                  const pub = s.articles.filter((a) => a.status === 'published').length;
                  return (
                    <Link
                      key={s.id}
                      to={`/series/${s.id}`}
                      className="flex items-start gap-3 p-4 border border-[#E8E0D5] rounded-lg hover:border-[#C8BFB5] hover:bg-[#F8F3EC] transition-all cursor-pointer group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-[#5B7B6F] font-medium" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{s.category}</span>
                          <span className="text-xs text-[#9A8E84]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>{pub}/{s.articles.length}本</span>
                        </div>
                        <h3 className="text-sm font-semibold text-[#2C2416] group-hover:text-[#5B7B6F] transition-colors leading-snug" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                          {s.seriesName}
                        </h3>
                      </div>
                      <i className="ri-arrow-right-line text-[#C8BFB5] group-hover:text-[#5B7B6F] transition-colors flex-shrink-0 mt-1"></i>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Bottom nav */}
          <div className="pt-4 border-t border-[#EDE7DF] flex flex-wrap gap-4">
            <Link to="/series" className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              <i className="ri-arrow-left-line text-[#5B7B6F]"></i>
              シリーズ一覧
            </Link>
            <span className="text-[#D4C9BE]">·</span>
            <Link to="/articles" className="inline-flex items-center gap-1.5 text-sm text-[#7A6E62] hover:text-[#2C2416] transition-colors cursor-pointer" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              記事一覧
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
